import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, LogOut } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { useLang } from "@/i18n";
import ExtendStay from "@/components/ExtendStay";

export default function OverdueDepartures({ compact = false, onCheckout, onExtended, refreshKey }) {
  const { t } = useLang();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(!compact);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/dashboard/overdue-departures").then((r) => setRows(r.data.rows || [])).catch(() => setRows([]));
  }, [refreshKey, tick]);

  if (!rows.length) return null;

  return (
    <div data-testid="overdue-departures"
      className="mb-4 rounded-2xl border border-rose-300/70 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-display font-bold text-rose-800 dark:text-rose-200">
          <AlertTriangle className="h-4 w-4" />
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-rose-600 px-1.5 text-xs font-bold text-white tabular-nums"
            data-testid="overdue-departures-count">{rows.length}</span>
          {t("overdue_dep_title")}
        </p>
        {compact && (
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="overdue-departures-toggle"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-rose-700">
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {open ? t("overdue_dep_hide") : t("overdue_dep_view")}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} data-testid={`overdue-row-${r.id}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-rose-200 dark:border-rose-900 bg-card px-3 py-2 text-xs">
              <span className="font-semibold text-primary">{r.pitch_label}</span>
              <span className="font-medium text-foreground">{r.guest_name}</span>
              <span className="text-muted-foreground">{fmtDate(r.departure)}</span>
              <span className="rounded-full bg-rose-100 dark:bg-rose-950/60 px-2 py-0.5 font-bold text-rose-700 dark:text-rose-300 tabular-nums">
                {r.days_late} {r.days_late === 1 ? t("overdue_dep_day") : t("overdue_dep_days")}
              </span>
              {r.outstanding > 0 && (
                <span className="tabular-nums text-amber-700 dark:text-amber-300" data-testid={`overdue-open-${r.id}`}>
                  {t("overdue_dep_open")}: {money(r.outstanding)}
                </span>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                <ExtendStay res={{ id: r.id, guest_name: r.guest_name, pitch_label: r.pitch_label, departure: r.departure }}
                  variant="outline" onDone={() => { setTick((x) => x + 1); onExtended?.(); }} />
                <button type="button" data-testid={`overdue-checkout-${r.id}`}
                  onClick={() => (onCheckout ? onCheckout(r) : nav(`/vertrekkend?id=${r.id}`))}
                  className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground transition-colors hover:opacity-90">
                  <LogOut className="h-3 w-3" />{t("checkout")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
